import { getFirestoreDb } from './firebase';
import type { Timestamp } from 'firebase/firestore';
import type { UsageTier } from './stripeService';
import { createLogger } from './loggingService';

const logger = createLogger('UsageService');

type FirestoreModule = typeof import('firebase/firestore');
let firestoreModulePromise: Promise<FirestoreModule> | null = null;

const loadFirestoreModule = () => {
  if (!firestoreModulePromise) {
    firestoreModulePromise = import('firebase/firestore');
  }
  return firestoreModulePromise;
};

const getFirestoreDeps = async () => {
  const [module, db] = await Promise.all([loadFirestoreModule(), getFirestoreDb()]);
  return { module, db };
};

export interface UsageData {
  freeRunsRemaining: number;
  paidRunsRemaining: number;
  totalRunsUsed: number;
  lastRunAt: Timestamp | null;
  purchaseHistory: Array<{
    tierId: UsageTier;
    runsAdded: number;
    purchasedAt: Timestamp;
    amount: number;
  }>;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Initialize usage tracking for a new user
 * Creates a usage document with 3 free runs
 */
export const initializeUserUsage = async (userId: string): Promise<void> => {
  try {
    const { module, db } = await getFirestoreDeps();
    const usageRef = module.doc(db, 'users', userId, 'usage', 'current');
    
    // Check if usage document already exists
    const usageSnap = await module.getDoc(usageRef);
    if (usageSnap.exists()) {
      logger.debug('Usage already initialized', { userId });
      return;
    }
    
    // Create new usage document with 3 free runs
    await module.setDoc(usageRef, {
      freeRunsRemaining: 3,
      paidRunsRemaining: 0,
      totalRunsUsed: 0,
      lastRunAt: null,
      purchaseHistory: [],
      createdAt: module.serverTimestamp(),
      updatedAt: module.serverTimestamp(),
    });
    
    logger.info('Initialized usage tracking', { userId });
  } catch (error) {
    logger.error('Error initializing user usage', { error: error instanceof Error ? error.message : String(error), userId });
    throw new Error('Failed to initialize usage tracking');
  }
};

/**
 * Check how many runs are available (free + paid)
 */
export const checkAvailableRuns = async (userId: string): Promise<{
  total: number;
  free: number;
  paid: number;
  hasRuns: boolean;
  }> => {
  try {
    const { module, db } = await getFirestoreDeps();
    const usageRef = module.doc(db, 'users', userId, 'usage', 'current');
    const usageSnap = await module.getDoc(usageRef);
    
    if (!usageSnap.exists()) {
      // No usage document - need to initialize
      return { total: 0, free: 0, paid: 0, hasRuns: false };
    }
    
    const usage = usageSnap.data() as UsageData;
    const free = usage.freeRunsRemaining || 0;
    const paid = usage.paidRunsRemaining || 0;
    const total = free + paid;
    
    return {
      total,
      free,
      paid,
      hasRuns: total > 0,
    };
  } catch (error) {
    logger.error('Error checking available runs', { error: error instanceof Error ? error.message : String(error), userId });
    throw new Error('Failed to check available runs');
  }
};

/**
 * Consume one analysis run
 * Uses free runs first, then paid runs
 * This should be called by Cloud Functions to prevent client-side manipulation
 */
export const consumeRun = async (userId: string): Promise<void> => {
  try {
    const { module, db } = await getFirestoreDeps();
    const usageRef = module.doc(db, 'users', userId, 'usage', 'current');
    const usageSnap = await module.getDoc(usageRef);
    
    if (!usageSnap.exists()) {
      throw new Error('Usage document does not exist. Please initialize usage tracking first.');
    }
    
    const usage = usageSnap.data() as UsageData;
    
    // Check if any runs are available
    const totalRuns = usage.freeRunsRemaining + usage.paidRunsRemaining;
    if (totalRuns <= 0) {
      throw new Error('No runs remaining. Please purchase more runs or subscribe.');
    }
    
    // Consume free runs first, then paid runs
    if (usage.freeRunsRemaining > 0) {
      await module.updateDoc(usageRef, {
        freeRunsRemaining: module.increment(-1),
        totalRunsUsed: module.increment(1),
        lastRunAt: module.serverTimestamp(),
        updatedAt: module.serverTimestamp(),
      });
    } else {
      await module.updateDoc(usageRef, {
        paidRunsRemaining: module.increment(-1),
        totalRunsUsed: module.increment(1),
        lastRunAt: module.serverTimestamp(),
        updatedAt: module.serverTimestamp(),
      });
    }
    
    logger.info('Consumed run', { userId });
  } catch (error) {
    logger.error('Error consuming run', { error: error instanceof Error ? error.message : String(error), userId });
    throw error;
  }
};

/**
 * Add purchased runs to user's account
 * This should be called by Cloud Functions after successful payment
 */
export const addPurchasedRuns = async (
  userId: string,
  tierId: UsageTier,
  runsToAdd: number,
  amountPaid: number
): Promise<void> => {
  try {
    const { module, db } = await getFirestoreDeps();
    const usageRef = module.doc(db, 'users', userId, 'usage', 'current');
    const usageSnap = await module.getDoc(usageRef);
    
    if (!usageSnap.exists()) {
      // Initialize usage if it doesn't exist
      await initializeUserUsage(userId);
    }
    
    // Add the purchased runs to paidRunsRemaining
    await module.updateDoc(usageRef, {
      paidRunsRemaining: module.increment(runsToAdd),
      purchaseHistory: module.arrayUnion({
        tierId,
        runsAdded: runsToAdd,
        purchasedAt: module.serverTimestamp(),
        amount: amountPaid,
      }),
      updatedAt: module.serverTimestamp(),
    });
    
    logger.info('Added purchased runs', { runsToAdd, userId, tierId });
  } catch (error) {
    logger.error('Error adding purchased runs', { error: error instanceof Error ? error.message : String(error), userId });
    throw new Error('Failed to add purchased runs');
  }
};

/**
 * Get full usage data for a user
 */
export const getUserUsage = async (userId: string): Promise<UsageData | null> => {
  try {
    const { module, db } = await getFirestoreDeps();
    const usageRef = module.doc(db, 'users', userId, 'usage', 'current');
    const usageSnap = await module.getDoc(usageRef);
    
    if (!usageSnap.exists()) {
      return null;
    }
    
    return usageSnap.data() as UsageData;
  } catch (error) {
    logger.error('Error getting user usage', { error: error instanceof Error ? error.message : String(error), userId });
    return null;
  }
};

/**
 * Check if a user can run an analysis
 * Returns true if they have runs available OR have an active subscription OR are an admin
 */
export const canUserRunAnalysis = async (
  userId: string,
  hasActiveSubscription: boolean,
  isAdmin: boolean = false
): Promise<{
  canRun: boolean;
  reason: string;
  runsRemaining?: number;
}> => {
  try {
    // Admin users have unlimited runs
    if (isAdmin) {
      return {
        canRun: true,
        reason: 'admin',
      };
    }
    
    // Active subscribers have unlimited runs
    if (hasActiveSubscription) {
      return {
        canRun: true,
        reason: 'unlimited',
      };
    }
    
    // Check usage for non-subscribers
    const available = await checkAvailableRuns(userId);
    
    if (available.hasRuns) {
      return {
        canRun: true,
        reason: 'has_runs',
        runsRemaining: available.total,
      };
    }
    
    return {
      canRun: false,
      reason: 'no_runs',
      runsRemaining: 0,
    };
  } catch (error) {
    logger.error('Error checking if user can run analysis', { error: error instanceof Error ? error.message : String(error), userId });
    return {
      canRun: false,
      reason: 'error',
      runsRemaining: 0,
    };
  }
};
